import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Waves, Sun, Users, Baby, Wind, Palmtree } from 'lucide-react';
import SEO from '../components/SEO';

const PlagesIleDeRe = () => {
  const plages = [
    {
      nom: "Plage du Trousse-Chemise",
      location: "Les Portes-en-Ré",
      distance: "3 km",
      icon: <Palmtree className="w-6 h-6" />,
      description: "L'une des plus belles plages de l'île avec son eau turquoise et son sable fin. Parfaite pour la baignade et les activités nautiques.",
      caracteristiques: ["Eau cristalline", "Surveillée l'été", "Parking", "Accessible PMR"],
      image: "/images/plages/plage-1.jpg"
    },
    {
      nom: "Plage de la Redoute",
      location: "Les Portes-en-Ré",
      distance: "2 km",
      icon: <Baby className="w-6 h-6" />,
      description: "Plage familiale idéale pour les enfants avec une pente douce et des eaux calmes. Proche des commerces et restaurants.",
      caracteristiques: ["Familiale", "Peu profonde", "Surveillée", "Club enfants"],
      image: "/images/plages/plage-2.jpg"
    },
    {
      nom: "Plage des Gollandières",
      location: "Loix",
      distance: "5 km",
      icon: <Wind className="w-6 h-6" />,
      description: "Plage sauvage et préservée, parfaite pour ceux qui recherchent la tranquillité. Spot réputé pour le kitesurf.",
      caracteristiques: ["Sauvage", "Kitesurf", "Naturiste", "Non surveillée"],
      image: "/images/plages/plage-3.jpg"
    },
    {
      nom: "Plage du Petit Bec",
      location: "Saint-Clément-des-Baleines",
      distance: "8 km",
      icon: <Waves className="w-6 h-6" />,
      description: "Face à l'océan Atlantique, cette plage offre des vagues parfaites pour le surf. Vue imprenable sur le phare des Baleines.",
      caracteristiques: ["Surf", "Vagues", "Panorama", "Sauvage"],
      image: "/images/plages/plage-4.jpg"
    },
    {
      nom: "Plage de la Conche des Baleines",
      location: "Saint-Clément-des-Baleines",
      distance: "9 km",
      icon: <Sun className="w-6 h-6" />,
      description: "Immense plage de sable fin s'étendant sur plusieurs kilomètres. Idéale pour les longues promenades et le farniente.",
      caracteristiques: ["Grande plage", "Sable fin", "Promenade", "Couchers de soleil"],
      image: "https://images.unsplash.com/photo-1471922694854-ff1b63b20054?w=800"
    },
    {
      nom: "Plage de Gros Jonc",
      location: "Sainte-Marie-de-Ré",
      distance: "12 km",
      icon: <Users className="w-6 h-6" />,
      description: "Plage animée avec de nombreux bars de plage et restaurants. Ambiance conviviale et festive en été.",
      caracteristiques: ["Bars de plage", "Restaurants", "Animée", "Surveillée"],
      image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800"
    }
  ];

  return (
    <>
      <SEO 
        title="Les Plus Belles Plages de l'Île de Ré | Guide Complet 2026"
        description="Découvrez les plus belles plages de l'Île de Ré : Trousse-Chemise, La Conche, Gros Jonc. Guide complet avec distances depuis notre villa, caractéristiques et conseils."
        keywords="plages Île de Ré, meilleures plages Ré, Trousse-Chemise, plage Conche Baleines, plages familiales Île de Ré, baignade Île de Ré"
        ogImage="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200"
        url="/plages-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/plages/plage-1.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Waves className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Les Plus Belles Plages de l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              100 km de côtes entre Atlantique et Pertuis - Découvrez nos coups de cœur
            </p>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            L'<strong>Île de Ré</strong> est réputée pour ses <strong>plages exceptionnelles</strong> : 
            sable fin, eaux translucides, dunes préservées. Que vous recherchiez une plage familiale 
            surveillée, un spot de surf, ou une crique sauvage pour la tranquillité, l'île offre 
            une diversité remarquable sur ses 100 kilomètres de côtes.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Depuis notre <strong>Villa Nomade aux Portes-en-Ré</strong>, toutes les plages sont facilement 
            accessibles à vélo ou en voiture. Découvrez notre sélection des plus belles plages, 
            avec distances, caractéristiques et conseils pratiques.
          </p>
        </section>

        {/* Liste des plages */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Notre sélection des meilleures plages</h2>
          <div className="space-y-8">
            {plages.map((plage, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="md:flex">
                  <div className="md:w-1/3">
                    <img 
                      src={plage.image} 
                      alt={plage.nom}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:w-2/3">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-[#c17a3a] mt-1">{plage.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{plage.nom}</h3>
                        <p className="text-gray-600">{plage.location} • {plage.distance} de la villa</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{plage.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {plage.caracteristiques.map((car, i) => (
                        <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                          {car}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Conseils pratiques */}
        <section className="mb-16 bg-blue-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Conseils pour profiter des plages</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <h3 className="font-semibold text-lg mb-2">🚴 Accès à vélo</h3>
              <p>L'Île de Ré compte 100 km de pistes cyclables. Toutes les plages sont accessibles à vélo depuis notre villa. Location disponible au village.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">🅿️ Stationnement</h3>
              <p>En juillet-août, les parkings sont payants et peuvent être saturés. Privilégiez le vélo ou arrivez tôt le matin.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">🏊 Marées</h3>
              <p>Consultez les horaires de marées. À marée haute, certaines plages disparaissent presque complètement. Marée basse = grandes étendues de sable.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">☀️ Meilleure période</h3>
              <p>Juin-septembre pour la baignade (eau 18-22°C). Hors saison : plages désertes, promenades ressourçantes.</p>
            </div>
          </div>
        </section>

        {/* CTA Villa */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Séjournez à 2 km des plus belles plages</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Notre Villa Nomade est idéalement située pour explorer toutes les plages de l'île. 
            Partez le matin à vélo, profitez de la plage, et rentrez vous détendre dans le spa de nage.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver votre séjour
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Découvrir la villa
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": "Plages de l'Île de Ré",
          "description": "Guide complet des plus belles plages de l'Île de Ré : Trousse-Chemise, La Conche, Gros Jonc et plus",
          "url": "https://villa-nomade-re.preview.emergentagent.com/plages-ile-de-re"
        })}
      </script>
    </>
  );
};

export default PlagesIleDeRe;
